import { computed, inject } from '@angular/core';
import { ToastService } from '@app/core/toast';
import { callConfig, withCalls } from '@ngrx-traits/signals';
import {
  patchState,
  signalStore,
  withComputed,
  withProps,
  withState,
} from '@ngrx/signals';
import { EnumStore } from '@app/features/options/enum';
import { FontFamily } from '@app/models/enum-types.model';
import type { EditorDefaultFont } from '@app/shared/rich-text-editor/models/editor-default-font';
import { TenantSettingDto } from '../model/tenant-setting';
import { TenantSettingsService } from '../service/tenant-settings.service';

type TenantSettingsState = {
  settings: TenantSettingDto[];
};

const initalState = {
  settings: [],
} as TenantSettingsState;

export const TenantSettingsStore = signalStore(
  withState(initalState),
  withProps(() => ({
    tenantSettingsService: inject(TenantSettingsService),
    enumStore: inject(EnumStore),
    toastService: inject(ToastService),
  })),
  withComputed((store) => ({
    defaultDifferentialTaxation: computed(() => {
      const settings = store.settings();

      if (settings.length === 0) {
        return null;
      }

      const setting = settings.find(
        (x) => x.settingType === 'StandardDifferentialTaxation'
      );

      if (!setting) {
        return null;
      }

      return Number(setting.value.replace(',', '.'));
    }),
    /** Default font of the tenant's PDFs, with the API's fallbacks (`PrintUtils`): Arial, no size. */
    printDefaultFont: computed((): EditorDefaultFont => {
      const settings = store.settings();
      const fontFamilyId = settings.find(
        (x) => x.settingType === 'StandardFontFamily'
      )?.value;
      const fontSize = settings.find(
        (x) => x.settingType === 'StandardFontSize'
      )?.value;

      const fontFamily =
        (fontFamilyId
          ? store.enumStore.enumForKeyById<FontFamily>(
              'FontFamily',
              Number(fontFamilyId)
            )?.key
          : null) || 'Arial';

      return {
        fontFamily: `'${fontFamily}'`,
        fontSize: fontSize ? `${fontSize.replace(',', '.')}pt` : null,
      };
    }),
  })),
  withCalls(({ tenantSettingsService, toastService, ...store }) => ({
    loadSettings: callConfig({
      call: () => tenantSettingsService.getForTenant(),
      mapError: () => 'An error occurred while loading the Tenant Settings',
      onSuccess: ({ result }) => {
        patchState(store, { settings: result });
      },
      onError: (error) => {
        console.error(error);
        toastService.error(
          'Tenant Settings Error',
          'An error occurred while loading the Tenant Settings'
        );
      },
    }),
  }))
);
